import { useCallback } from 'react';
import type { MutableRefObject } from 'react';
import {
  pointerWithin,
  rectIntersection,
  closestCenter,
  getFirstCollision,
  type CollisionDetection,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import { layoutCellKey, parseOverTarget, taskDndId, type EkLayout } from '@/utils/easykit';

interface UseEasykitCollisionDetectionArgs {
  activeTaskId: number | null;
  dragLayoutRef: MutableRefObject<EkLayout | null>;
  lastOverIdRef: MutableRefObject<UniqueIdentifier | null>;
  recentlyMovedToNewContainerRef: MutableRefObject<boolean>;
}

/**
 * dnd-kit multiple-containers collision strategy (Trello-board recipe).
 *
 *   pointerWithin → if no pointer hits, rectIntersection.
 *   If first collision is a cell with items → narrow via closestCenter to
 *   the closest task inside that cell, so the over-id is a task (with index)
 *   not the cell (which would always append).
 *   lastOverId + recentlyMovedToNewContainer guards keep entering an empty
 *   container stable (no per-frame flicker).
 */
export function useEasykitCollisionDetection({
  activeTaskId,
  dragLayoutRef,
  lastOverIdRef,
  recentlyMovedToNewContainerRef,
}: UseEasykitCollisionDetectionArgs): CollisionDetection {
  return useCallback<CollisionDetection>((args) => {
    if (activeTaskId === null) return closestCenter(args);

    const pointer = pointerWithin(args);
    const intersections = pointer.length > 0 ? pointer : rectIntersection(args);
    let overId = getFirstCollision(intersections, 'id');

    if (overId != null) {
      const overStr = String(overId);
      if (overStr.startsWith('cell:')) {
        const target = parseOverTarget(overStr);
        if (target?.kind === 'cell') {
          const items = (dragLayoutRef.current?.get(layoutCellKey(target.driverId, target.dateKey)) ?? []).map(taskDndId);
          if (items.length > 0) {
            const closest = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (c) => c.id !== overId && items.includes(String(c.id)),
              ),
            });
            if (closest.length > 0) overId = closest[0].id;
          }
        }
      }
      lastOverIdRef.current = overId;
      return [{ id: overId }];
    }

    if (recentlyMovedToNewContainerRef.current) {
      lastOverIdRef.current = taskDndId(activeTaskId);
    }
    return lastOverIdRef.current != null ? [{ id: lastOverIdRef.current }] : [];
  }, [activeTaskId, dragLayoutRef, lastOverIdRef, recentlyMovedToNewContainerRef]);
}
